'use client';
import { BrandDocument } from '@models/brand';
import { CategoryDocument } from '@models/category';
import Breadcrumb from '@ui/Breadcrumb';
import SafeImage from '@ui/SafeImage';
import { ShopByVehicle } from '@ui/ShopByVehicle';
import ShopYourGarage from '@ui/ShopYourGarage';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import { useFitmentFilterSync } from '@util/hooks/useFitmentFilterSync';
import useProductsPage, {
  PageSideFilters,
  PageType,
  TopFilters,
} from '@util/hooks/useProductsPage';
import { Pagination } from '@util/types/paginator';
import { cn } from '@util/index';
import MobileSearchFilters from 'app/search/components/MobileSearchFilters';
import MobileSearchSort from 'app/search/components/MobileSearchSort';
import SearchControls from 'app/search/components/SearchControls';
import { useBikeFilter } from 'context/BikeFilterContext';
import { VehicleFilterContext } from 'context/VehicleFilterContext';
import { CuratedListDocument } from 'models/curated_list';
import { FacetCounts, ProductLight, ProductSortOption } from 'models/product';
import { UserBike } from 'models/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaCalendarAlt,
  FaSortAmountDown,
  FaSortAmountUpAlt,
  FaStar,
} from 'react-icons/fa';
import Filters from './filters/Filters';
import FloatingMobileFilter from './FloatingMobileFilter';
import PageMainContent from './products/PageMainContent';

export const sortingOptions: {
  label: string;
  value: ProductSortOption;
  id: string;
  icon: JSX.Element;
}[] = [
  {
    label: 'Newest Listings',
    value: 'newest',
    id: 'newest',
    icon: <FaCalendarAlt className="size-5 text-zinc-400" />,
  },
  {
    label: 'Price: Low to High',
    value: 'priceAsc',
    id: 'priceAsc',
    icon: <FaSortAmountUpAlt className="size-5 text-zinc-400" />,
  },
  {
    label: 'Price: High to Low',
    value: 'priceDesc',
    id: 'priceDesc',
    icon: <FaSortAmountDown className="size-5 text-zinc-400" />,
  },
  {
    label: 'Best Match',
    value: 'bestMatch',
    id: 'bestMatch',
    icon: <FaStar className="size-5 text-zinc-400" />,
  },
  {
    label: 'Sold Date',
    value: 'soldDate',
    id: 'soldDate',
    icon: <FaCalendarAlt className="size-5 text-zinc-400" />,
  },
];

export const sellerTypeOptions = [
  {
    label: 'All',
    value: 'all',
    key: 'all',
  },
  {
    label: 'Individual',
    value: 'individual',
    key: 'individual',
  },
  {
    label: 'Dealer',
    value: 'dealer',
    key: 'dealer',
  },
];

export const itemsPerPageOptions = [
  {
    label: '12',
    value: '12',
    id: '12',
  },
  {
    label: '24',
    value: '24',
    id: '24',
  },
  {
    label: '48',
    value: '48',
    id: '48',
  },
];

export const PageHeader = ({
  pageHeaderTitle,
  pageHeaderBody,
  pageHeaderImage,
  pageHeaderGradient = 'bg-gradient-to-r from-white via-white to-transparent',
  pageHeaderBaseBackground = 'bg-inherit',
  pageHeaderDescription,
  pageHeaderTitleSize,
}: {
  headerBreadcrumb: { label: string; href: string };
  pageHeaderTitle: string | React.ReactNode;
  pageHeaderBody?: string;
  pageHeaderImage?: string;
  pageHeaderGradient?: string;
  pageHeaderBaseBackground?: string;
  pageHeaderDescription?: string;
  pageHeaderTitleSize?: string;
}) => {
  return (
    <>
      <div
        className={cn(
          'relative flex h-fit w-full items-center justify-center overflow-clip py-4 transition-colors sm:justify-start',
          pageHeaderBaseBackground
        )}
      >
        <div
          className={cn(
            'relative z-10 flex w-full max-w-content items-center justify-between text-black',
            pageHeaderImage && pageHeaderGradient
          )}
        >
          <div className="px-8 sm:px-0">
            <h1
              className={cn(
                'my-8 font-semibold',
                pageHeaderTitleSize ?? 'text-[2.4rem] sm:text-[3.2rem]'
              )}
            >
              {pageHeaderTitle}
            </h1>

            <div className="hidden sm:flex">
              {pageHeaderDescription && (
                <div className="my-4 text-[12px] text-zinc-500">
                  {pageHeaderDescription}
                </div>
              )}
            </div>

            {pageHeaderBody && (
              <div className="hidden sm:flex">
                <div
                  className="my-4 text-[12px] text-zinc-500"
                  dangerouslySetInnerHTML={{
                    __html: pageHeaderBody,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const BrandHeader = ({ brand }: { brand: BrandDocument }) => {
  return (
    <>
      <div className="relative flex h-fit w-full items-center justify-center overflow-clip transition-colors sm:justify-start">
        <div className=" relative z-10 mx-auto flex min-h-[16rem] w-full max-w-content items-center justify-between px-[1.6rem] sm:min-h-[20rem] sm:px-0 sm:pl-[12rem]">
          <div className="pt-10 sm:mr-[6.4rem]">
            <Breadcrumb
              items={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Brands',
                  href: '/brands',
                },
                {
                  label: brand.name,
                  href: '/brands/' + brand.url,
                  current: true,
                },
              ]}
            />
            <div className="mt-6 flex flex-col gap-[1.6rem] sm:flex-row sm:items-center">
              {brand.image?.startsWith('http') && (
                <div className="relative h-[10rem] w-[10rem] sm:h-[20rem] sm:w-[50rem]">
                  <SafeImage
                    className="mr-7 object-contain"
                    src={brand.image}
                    priority
                    fill
                    alt={brand.name}
                  />
                </div>
              )}

              <div>
                <h1 className="sm:text-header3 text-h2 font-semibold">
                  {brand.name}
                </h1>
                {brand.description && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: brand.description,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface ProductsPageProps {
  categories?: CategoryDocument[];
  initialProducts: ProductLight[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  headerBreadcrumb?: { label: string; href: string };
  pageHeaderTitle?: string | React.ReactNode;
  pageHeaderTitleSize?: string;
  pageHeaderDescription?: string;
  pageHeaderImage?: string;
  pageHeaderGradient?: string;
  pageHeaderBaseBackground?: string;
  pageHeaderBody?: string;
  brand?: BrandDocument;
  pageType: PageType;
  keyword: string | null;
  seller_id: string | null;
  // allBrands?: string[];
  children?: React.ReactNode;
  /**
   * Replaces the standard filter column. The live filter state is handed in
   * because this page owns it — a column that cannot read or write it would
   * only be a picture of one.
   */
  filters?: (state: {
    sideFilters: PageSideFilters;
    onChange: (filters: PageSideFilters) => void;
  }) => React.ReactNode;
  /** Off where the results are vehicles, which fit onto nothing. */
  showVehicleFitment?: boolean;
  /**
   * What the vehicle bar asks on mobile while no vehicle is chosen, where the
   * results cannot be narrowed until one is. On desktop the page asks with a
   * row of bikes instead. Shows the bar even where it would not otherwise be.
   */
  vehiclePrompt?: { title: string; description: string };
  controls?: React.ReactNode;
  curated_list?: CuratedListDocument;
  // sellers: {
  //   [key: string]: PublicUserDocument;
  // };
  showSearch?: boolean;
  bottomText?: string;
  facet_counts?: FacetCounts;
  mostly_parts?: boolean;
}
const ProductsPage = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  categories,
  pageHeaderTitle,
  pageHeaderTitleSize,
  pageHeaderDescription,
  pageHeaderImage,
  pageHeaderGradient,
  pageHeaderBaseBackground,
  pageHeaderBody,
  brand,
  pageType,
  keyword,
  seller_id,
  children,
  filters,
  showVehicleFitment = true,
  vehiclePrompt,
  controls,
  curated_list,
  headerBreadcrumb,
  facet_counts,
  // allBrands,
  bottomText,
  showSearch,
  mostly_parts,
}: ProductsPageProps) => {
  const {
    buttonToggles,
    sideFilters,
    topFilters,
    handleSideFiltersChange,
    handleSortChange,
    handleSearchChange,
    setDrawerOpen,
    isFetching,
    setIsFetching,
    clearAllFilters,
  } = useProductsPage({
    current_cat: null,
    keyword,
    category: null,
    category1: null,
    category2: null,
    category3: null,
    results: initialProducts,
    pageInfo: initialPageInfo,
    initialSideFilters,
    initialTopFilters,
    color: initialSideFilters.color,
    categories: categories ?? [],
    is_featured: pageType === 'featured',
    on_sale: pageType === 'onsale',
    is_auction: pageType === 'auctions',
    curated_list:
      curated_list && pageType === 'curated' ? curated_list.slug : null,
    seller_id,
    brands: [],
    facet_counts: facet_counts,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showMobileEditGarageModal, setShowMobileEditGarageModal] =
    useState(false);
  const searchControlsRef = useRef<HTMLDivElement>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const { setSelectedBike, clearSelectedBike, setFitmentFilterEnabled } =
    useBikeFilter();

  const urlBikeYear = initialSideFilters.bikeYears?.[0];
  const urlBikeMake = initialSideFilters.bikeMakes?.[0];
  const urlBikeModel = initialSideFilters.bikeModels?.[0];

  // A vehicle reaches the URL without anyone having chosen it here — a parsed
  // query writes one, a shared link carries one — and the fitment card is the
  // only place it can be seen, or taken back off.
  useEffect(() => {
    if (!urlBikeYear || !urlBikeMake || !urlBikeModel) return;

    setSelectedBike({
      year: Number(urlBikeYear),
      make: urlBikeMake as UserBike['make'],
      model: urlBikeModel,
    });
  }, [urlBikeYear, urlBikeMake, urlBikeModel, setSelectedBike]);

  const onBikeComplete = useCallback(
    (bike: UserBike | null) => {
      if (bike === null) {
        handleSideFiltersChange({
          ...sideFilters,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
          bikeTypes: [],
          rideTypes: [],
        });
        clearSelectedBike();
        return;
      }

      if (!bike.year || !bike.make || !bike.model) return;

      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [`${bike.year}`],
        bikeMakes: [bike.make],
        bikeModels: [bike.model!],
        bikeTypes: [bike.type],
      });
      setSelectedBike(bike);
    },
    [handleSideFiltersChange, sideFilters, setSelectedBike, clearSelectedBike]
  );

  const onToggleBikeFilter = useCallback(
    (enabled: boolean) => {
      // unchecking keeps the vehicle in the bar so it can be turned back on for
      // the next search without picking it again
      setFitmentFilterEnabled(enabled);

      if (enabled) return;

      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [],
        bikeMakes: [],
        bikeModels: [],
        bikeTypes: [],
        rideTypes: [],
      });
    },
    [handleSideFiltersChange, sideFilters, setFitmentFilterEnabled]
  );

  const handleFilterRemove = useCallback(
    (filter: { key: string; value: any }) => {
      const { key, value } = filter;
      const newFilters = { ...sideFilters };
      if (Array.isArray(newFilters[key as keyof typeof newFilters])) {
        (newFilters[key as keyof typeof newFilters] as any[]) = (
          newFilters[key as keyof typeof newFilters] as any[]
        ).filter((v) => v !== value);
      } else if (
        typeof newFilters[key as keyof typeof newFilters] === 'object' &&
        newFilters[key as keyof typeof newFilters] !== null
      ) {
        delete (
          newFilters[key as keyof typeof newFilters] as { [key: string]: any }
        )[value];
      } else {
        delete newFilters[key as keyof typeof newFilters];
      }
      handleSideFiltersChange(newFilters);
    },
    [sideFilters, handleSideFiltersChange]
  );

  const {
    handleFilterChange,
    handleCategoryChange,
    handleBikeSelection,
    handleSizeChange,
    handlePriceRangeChange,
    activePage,
    setActivePage,
    pendingFilterChanges,
    setPendingFilterChanges,
  } = useCategoryFilters({
    filterState: sideFilters,
    setFilterState: handleSideFiltersChange,
  });

  const shouldShowShopByVehicle =
    showVehicleFitment &&
    (mostly_parts ||
      !!vehiclePrompt ||
      !!initialSideFilters.bikeModels?.length ||
      !!sideFilters.bikeModels?.length ||
      pageType === 'profile');

  useFitmentFilterSync({
    filters: sideFilters,
    onBikeComplete,
    enabled: shouldShowShopByVehicle,
    // a seller's storefront is browsed as a whole, so a vehicle picked
    // elsewhere shouldn't quietly hide most of their listings
    autoApply: pageType !== 'profile',
  });

  return (
    <>
      <div className="w-full sm:mx-auto sm:max-w-content">
        {seller_id ? (
          <></>
        ) : brand ? (
          <BrandHeader brand={brand} />
        ) : headerBreadcrumb && pageHeaderTitle ? (
          <PageHeader
            headerBreadcrumb={headerBreadcrumb}
            pageHeaderTitle={pageHeaderTitle}
            pageHeaderTitleSize={pageHeaderTitleSize}
            pageHeaderDescription={pageHeaderDescription}
            pageHeaderImage={pageHeaderImage}
            pageHeaderBody={pageHeaderBody}
            pageHeaderGradient={pageHeaderGradient}
            pageHeaderBaseBackground={pageHeaderBaseBackground}
          />
        ) : (
          <></>
        )}
      </div>

      {shouldShowShopByVehicle && (
        <div className="w-full">
          <div className="block w-full sm:hidden">
            <ShopYourGarage
              onBikeComplete={onBikeComplete}
              onToggleBikeFilter={onToggleBikeFilter}
              sideFilters={sideFilters}
              handleFilterRemove={handleFilterRemove}
              showMobileEditGarageModal={showMobileEditGarageModal}
              setShowMobileEditGarageModal={setShowMobileEditGarageModal}
              prompt={vehiclePrompt}
            />
          </div>

          <div className="hidden w-full sm:block">
            <ShopByVehicle
              onBikeComplete={onBikeComplete}
              onToggleBikeFilter={onToggleBikeFilter}
              sideFilters={sideFilters}
            />
          </div>
        </div>
      )}

      <VehicleFilterContext.Provider value={onBikeComplete}>
        {children}
      </VehicleFilterContext.Provider>
      <PageMainContent
        view={topFilters.view}
        isFetching={isFetching}
        clearAllFilters={clearAllFilters}
        setIsFetching={setIsFetching}
        products={initialProducts}
        pageInfo={initialPageInfo}
        full={!!seller_id}
        filterState={sideFilters}
        attribution={{
          data_source: 'typesense',
          page: pageType,
          section:
            pageType === 'search' && keyword
              ? keyword
              : `products-${topFilters.view}`,
        }}
        onFiltersChange={handleSideFiltersChange}
        searchControlsRef={searchControlsRef}
        filters={
          filters?.({ sideFilters, onChange: handleSideFiltersChange }) ?? (
            <div className="w-[38rem]">
              <Filters
                categories={categories}
                pageType={pageType}
                filterState={sideFilters}
                handleFilterChange={handleFilterChange}
                handleCategoryChange={handleCategoryChange}
                handleBikeSelection={handleBikeSelection}
                handleSizeChange={handleSizeChange}
                pendingFilterChanges={pendingFilterChanges}
                setPendingFilterChanges={setPendingFilterChanges}
                mostly_parts={mostly_parts}
                facet_counts={facet_counts}
                handlePriceRangeChange={handlePriceRangeChange}
                activePage={activePage}
                setActivePage={setActivePage}
              />
            </div>
          )
        }
        controls={
          controls || (
            <SearchControls
              keyword={keyword ?? ''}
              handleConditionsChange={(value) =>
                handleFilterChange('conditions', value)
              }
              handleSellerTypeChange={(value) =>
                handleFilterChange('sellerType', value)
              }
              handleSortChange={handleSortChange}
              handleSaleChange={(value) => handleFilterChange('onsale', value)}
              setDrawerOpen={setDrawerOpen}
              topFilters={topFilters}
              showSearch={pageType === 'profile'}
              pageType={pageType}
              dropdownOpen={dropdownOpen}
              buttonToggles={buttonToggles}
              handleSearchChange={handleSearchChange}
              setDropdownOpen={setDropdownOpen}
              filterState={sideFilters}
              setFilterState={handleSideFiltersChange}
              currentCat={null}
              facet_counts={facet_counts}
              mostly_parts={mostly_parts}
              categories={categories ?? []}
            />
          )
        }
        hidePaginator={false}
      />

      {/* Calculate if there are active filters */}
      {(() => {
        const hasActiveFilters =
          sideFilters.conditions?.length > 0 ||
          sideFilters.brands?.length > 0 ||
          (sideFilters.category && sideFilters.category.length > 0) ||
          (sideFilters.product_cats?.category &&
            sideFilters.product_cats.category.length > 0) ||
          (sideFilters.product_cats?.category1 &&
            sideFilters.product_cats.category1.length > 0) ||
          (sideFilters.product_cats?.category2 &&
            sideFilters.product_cats.category2.length > 0) ||
          (sideFilters.product_cats?.category3 &&
            sideFilters.product_cats.category3.length > 0) ||
          (sideFilters.letterSizes && sideFilters.letterSizes.length > 0) ||
          (sideFilters.numberSizes && sideFilters.numberSizes.length > 0) ||
          (sideFilters.sellerType && sideFilters.sellerType.length > 0) ||
          (sideFilters.bikeYears && sideFilters.bikeYears.length > 0) ||
          (sideFilters.bikeMakes && sideFilters.bikeMakes.length > 0) ||
          (sideFilters.bikeModels && sideFilters.bikeModels.length > 0) ||
          sideFilters.onsale ||
          sideFilters.recentlySold;

        return (
          <FloatingMobileFilter
            filterSectionRef={searchControlsRef}
            onFilterClick={() => setShowMobileFilters(true)}
            onSortClick={() => setShowMobileSort(true)}
            hasActiveFilters={hasActiveFilters}
          />
        );
      })()}

      {/* Mobile filter drawer */}
      <MobileSearchFilters
        showFilters={showMobileFilters}
        setShowFilters={setShowMobileFilters}
        categories={categories || []}
        filterState={sideFilters}
        setFilterState={handleSideFiltersChange}
        facet_counts={facet_counts}
        mostly_parts={mostly_parts}
        pageType={pageType || 'search'}
        filters={filters?.({
          sideFilters,
          onChange: handleSideFiltersChange,
        })}
      />

      {/* Mobile sort drawer */}
      <MobileSearchSort
        show={showMobileSort}
        setShow={setShowMobileSort}
        sortingBy={topFilters.sort}
        onSortingChange={handleSortChange}
        pageType={pageType || 'search'}
        filterState={sideFilters}
      />

      {(curated_list?.bottom_text || bottomText) && (
        <div className="mt-16 w-full border-b border-zinc-200 bg-[#F4F4F4] py-8">
          <div className="w-full sm:mx-auto sm:max-w-content">
            <div className=" w-full gap-[1.2rem] px-8 sm:block sm:px-0">
              <div className="flex gap-x-[1.5rem] py-12 sm:mx-auto sm:max-w-content">
                <div>
                  {curated_list?.title && (
                    <h2 className="mb-2 text-left text-[2rem] font-semibold">
                      {curated_list.title}
                    </h2>
                  )}

                  <div
                    className=" text-zinc-600"
                    dangerouslySetInnerHTML={{
                      __html:
                        (curated_list?.bottom_text ?? bottomText)
                          ?.replaceAll(
                            /{size}/g,
                            `${
                              sideFilters?.numberSizes[0] ||
                              sideFilters?.letterSizes[0]
                                ? `size ${
                                    sideFilters.numberSizes[0] ||
                                    sideFilters.letterSizes[0]
                                  }`
                                : ''
                            }`
                          )
                          .replaceAll(
                            /{brand}/g,
                            `${
                              sideFilters?.brands[0]
                                ? sideFilters.brands[0]
                                : ''
                            }`
                          ) ?? '',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
